import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import FocusLock from "react-focus-lock";
import { Button } from "@/shared/ui";
import { PIN_LENGTH } from "@/domains/auth/constants/other.ts";
import AuthPin from "@/domains/auth/components/AuthPin.tsx";
import AuthTitle from "@/domains/auth/components/AuthTitle.tsx";

interface AuthUpdatePinProps {
  title: string;
  pin: string;
  onPinChange: (value: string) => void;
  pinRepeat: string;
  onPinRepeatChange: (value: string) => void;
  onSubmit: (newPin: string) => void;
  error?: string;
  pending: boolean;
  onMatched?: () => void;
  onNotMatched?: () => void;
}

function AuthUpdatePin({
  title,
  pinRepeat,
  pin,
  onPinChange,
  onPinRepeatChange,
  onSubmit,
  error = "",
  pending,
  onMatched,
  onNotMatched,
}: AuthUpdatePinProps) {
  const [showRepeatPin, setShowRepeatPin] = useState(false);

  const switchToRepeatPin = useCallback(
    debounce(() => {
      setShowRepeatPin(true);
    }, 300),
    [],
  );

  const switchToPin = useCallback(() => {
    onPinChange("");
    onPinRepeatChange("");
    setShowRepeatPin(false);
  }, []);

  useEffect(() => {
    if (pin.length === PIN_LENGTH && !showRepeatPin) {
      switchToRepeatPin();
    }
  }, [pin, showRepeatPin, switchToRepeatPin]);

  const handleNotMatched = useCallback(() => {
    const pinField = document.getElementById("pin-field");
    if (pinField) {
      pinField.dataset.invalid = "";
      const onAnimationEnd = () => {
        delete pinField.dataset.invalid;
        pinField.removeEventListener("animationend", onAnimationEnd);
      };
      pinField.addEventListener("animationend", onAnimationEnd);
    }
    onNotMatched?.();
  }, [onNotMatched]);

  useEffect(() => {
    if (pinRepeat.length === PIN_LENGTH) {
      if (pin !== pinRepeat) {
        handleNotMatched();
      } else {
        onMatched?.();
      }
    }
  }, [onMatched, handleNotMatched, pin, pinRepeat]);

  const isDisabledEnter = useMemo(
    () => pinRepeat.length !== PIN_LENGTH && pin !== pinRepeat,
    [pinRepeat, pin],
  );

  useEffect(() => {
    if (pinRepeat.length === PIN_LENGTH && pin === pinRepeat) {
      onSubmit(pinRepeat);
    }
  }, [pin, pinRepeat, onSubmit]);

  return (
    <>
      <AuthTitle back={showRepeatPin ? switchToPin : undefined}>{title}</AuthTitle>
      <FocusLock className="contents">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (isDisabledEnter) return;
            onSubmit(pinRepeat);
          }}
          className="gap-3 grow flex flex-col"
        >
          {showRepeatPin ? (
            <AuthPin code={pinRepeat} onCodeChange={onPinRepeatChange} title="Повторите пин-код:" />
          ) : (
            <AuthPin code={pin} onCodeChange={onPinChange} title="Придумайте пин-код:" />
          )}
          <div className="text-center text-white/60 mt-2 font-medium">
            Установите пин-код из {PIN_LENGTH}х цифр, в дальнейшем он будет использоваться для входа
            в игру
          </div>
          <div className="mt-auto grid gap-2.5">
            {!!error && <div className="alert alert--danger">*{error}</div>}
            <Button submit className="btn--md w-full" loading={pending} disabled={isDisabledEnter}>
              Войти в игру
            </Button>
          </div>
        </form>
      </FocusLock>
    </>
  );
}

export default AuthUpdatePin;
